import {
  Column,
  Columns,
  InputText,
  SelectionList,
} from '@dixa/lib-shared-habla';
import { SelectionListChangeEvent } from '@dixa/lib-shared-habla';
import { ConversationLanguageCode } from 'lib-shared-languages';
import {
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { DeepWritable } from 'ts-essentials';

import {
  LanguageSelectionListValue,
  useLanguageSelectionList,
  UseLanguageSelectionListOptions,
} from 'utils/useLanguageSelectionList';
import { useLanguageSelectionListItems } from 'utils/useLanguageSelectionListItems';
import { useServerStorage } from 'utils/useServerStorage';

export type OnRevert = () => void;

export type OnLanguageChangeEvent = {
  /** The selected language. */
  readonly selectedLanguage?: LanguageSelectionListValue;
};

export type OnLanguageChange = (event: OnLanguageChangeEvent) => void;

export type TranslateTextLanguageSelectionListProps = {
  /** The selected language code. */
  selectedLanguageCode?: ConversationLanguageCode;
  /** The revert item label. */
  revertLabel?: ReactNode;
  /** Whether to auto focus the search field. */
  autoFocusSearch?: boolean;
  /** The callback for reverting to the original text. */
  onRevert?: OnRevert;
  /** The callback for when the language selection changes. */
  onLanguageChange?: OnLanguageChange;
  /** The function for sorting the languages. */
  sortFn?: (
    a: LanguageSelectionListValue,
    b: LanguageSelectionListValue,
  ) => number;
};

/**
 * Represents a selection list of languages for translating text to a selected
 * language.
 */
export const TranslateTextLanguageSelectionList = (
  props: TranslateTextLanguageSelectionListProps,
): JSX.Element => {
  const {
    autoFocusSearch,
    revertLabel = 'Show original message',
    selectedLanguageCode,
    onLanguageChange,
    onRevert,
    sortFn,
  } = props;

  const [searchTerm, setSearchTerm] = useState('');
  const [isActive, setIsActive] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const languageSelectionListOptions = useMemo<UseLanguageSelectionListOptions>(
    () => ({
      selectedLanguageCodes: selectedLanguageCode
        ? [selectedLanguageCode]
        : undefined,
    }),
    [selectedLanguageCode],
  );
  const { data, set } = useServerStorage('user');
  const recentlyUsedLanguagesCodes = useMemo(
    () => data.agent_copilot?.recentlyUsedLanguageCodes ?? [],
    [data.agent_copilot],
  );

  const { languages, selectedLanguages, clearSelection, onChange } =
    useLanguageSelectionList(languageSelectionListOptions);

  const showRevert = !!(onRevert && selectedLanguages.length);

  const languageSelectionListItems = useLanguageSelectionListItems({
    languages,
    searchTerm,
    recentlyUsedLanguagesCodes,
    revertLabel,
    showRevert,
    sortFn,
  });

  const handleOnChange = useCallback(
    async (event: SelectionListChangeEvent<LanguageSelectionListValue>) => {
      const { selectedItem } = event;
      const { value } = selectedItem;

      set({
        agent_copilot: (draft) => {
          draft.recentlyUsedLanguageCodes = Array.from(
            new Set([
              event.selectedItem?.value.code ?? null,
              ...recentlyUsedLanguagesCodes,
            ]),
          ).slice(0, 5);
        },
      });

      if (onRevert && typeof value === 'string' && value === 'revert') {
        onLanguageChange?.({});
        onRevert();
        clearSelection();
        return;
      }

      const selectedLanguage = value;

      if (
        selectedLanguages.some(({ code }) => selectedLanguage.code === code)
      ) {
        return;
      }

      onLanguageChange?.({ selectedLanguage });
      onChange(event);
    },
    [
      clearSelection,
      onChange,
      onLanguageChange,
      onRevert,
      recentlyUsedLanguagesCodes,
      selectedLanguages,
      set,
    ],
  );

  useEffect(() => {
    if (autoFocusSearch && inputRef.current) {
      inputRef.current.focus();
    }
  }, [autoFocusSearch]);

  return (
    <Columns style={{ height: 260, width: 320 }}>
      <Column>
        <Column.Row
          noHorizontalPadding
          noVerticalPadding
          noMinHeight
          style={{
            padding: 'var(--dixa-size-spacer-200)',
          }}
        >
          <InputText
            ref={inputRef}
            value={searchTerm}
            onChange={(event) => setSearchTerm(event.target.value)}
            onFocus={() => setIsActive(true)}
            onBlur={() => setIsActive(false)}
          />
        </Column.Row>

        <Column.Row
          noHorizontalPadding
          noVerticalPadding
          noMinHeight
          scrollable
        >
          <SelectionList<LanguageSelectionListValue>
            isActive={isActive}
            style={{
              padding: 'var(--dixa-size-spacer-200)',
            }}
            selectedValues={
              selectedLanguages as DeepWritable<typeof selectedLanguages>
            }
            onChange={handleOnChange}
          >
            {languageSelectionListItems}
          </SelectionList>
        </Column.Row>
      </Column>
    </Columns>
  );
};
